/**
 * Certificados (spec 011, secao 4): o que a equipe emitiu pra esta pessoa. O arquivo vem pela API autenticada (nunca
 * por link publico) e cada download fica na trilha. Os certificados do sistema antigo continuam no sistema antigo.
 */
import { Aviso, Botao, Carregando, Cartao, formatarBytes, formatarDia, Paginacao, paginar, Pilha } from "@mtc/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useApi } from "../acoes";
import { useSessao } from "../sessao";
import { usePagina } from "../pagina";

type Certificado = { id: string; titulo: string; emitido_em: string; observacao: string | null; arquivo_nome: string; tipo: string; tamanho: number; criado_em: string };
/** O sistema antigo emitia imagem; o novo aceita PDF e imagem (spec 013, item 5). */
const formato = (tipo: string) => (tipo.startsWith("image/") ? "Imagem" : "PDF");

export function PaginaCertificados() {
  const pag = usePagina();
  const { ler } = useApi();
  const { arquivo } = useSessao();
  const [erro, setErro] = useState<string>();
  const [baixando, setBaixando] = useState<string>();
  const q = useQuery({ queryKey: ["certificados"], queryFn: () => ler<{ itens: Certificado[] }>("/v1/me/certificados") });
  const baixar = async (c: Certificado) => {
    setErro(undefined);
    setBaixando(c.id);
    try {
      const blob = await arquivo(`/v1/me/certificados/${c.id}/arquivo`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = c.arquivo_nome;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch {
      setErro("Não consegui baixar agora. Tente de novo em instantes.");
    } finally {
      setBaixando(undefined);
    }
  };
  return (
    <Pilha espaco={6}>
      <header style={{ display: "grid", gap: "var(--e-2)" }}>
        <h1 className="mtc-titulo mtc-cabecalho__titulo">Certificados</h1>
      </header>
      {erro && <Aviso tipo="erro">{erro}</Aviso>}
      {q.isLoading ? (
        <Carregando compacto texto="Buscando seus certificados" />
      ) : q.isError ? (
        <Aviso tipo="erro">Não consegui buscar seus certificados agora.</Aviso>
      ) : !q.data?.itens.length ? (
        <Cartao><p className="mtc-texto-2" style={{ margin: 0 }}>Você ainda não tem certificado. Quando a equipe emitir um, ele aparece aqui pra baixar.</p></Cartao>
      ) : (
        <>
        {paginar(q.data.itens, pag.pagina).map((c) => (
          <Cartao key={c.id}>
            <Pilha espaco={3}>
              <div style={{ display: "grid", gap: 2 }}>
                <strong style={{ fontSize: "var(--t-md)" }}>{c.titulo}</strong>
                <span className="mtc-texto-2" style={{ fontSize: "var(--t-sm)" }}>Emitido em {formatarDia(c.emitido_em)} · {formato(c.tipo)} · {formatarBytes(c.tamanho)}</span>
                {c.observacao && <span className="mtc-texto-2" style={{ fontSize: "var(--t-sm)" }}>{c.observacao}</span>}
              </div>
              <div><Botao variante="secundario" carregando={baixando === c.id} onClick={() => void baixar(c)}>{c.tipo.startsWith("image/") ? "Baixar a imagem" : "Baixar o PDF"}</Botao></div>
            </Pilha>
          </Cartao>
        ))}
        <Paginacao pagina={pag.pagina} total={q.data.itens.length} aoMudar={pag.aoMudar} rotulo="Páginas dos certificados" />
        </>
      )}
    </Pilha>
  );
}
